/**
 * useReservationsList — TanStack Query hook over
 * `GET /organizations/{slug}/reservations`.
 *
 * The shape mirrors `useProperties` (which mirrors `useUsersList`'s
 * closed-status union: loading / ready / forbidden / error). That pattern
 * is load-bearing for the forbidden-vs-error branching: a 403 means "you
 * lost `booking.reservations.read` since `/auth/me` cached" and the page
 * renders a permission-denied panel instead of the generic Retry
 * affordance.
 *
 * The hook:
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per CLAUDE.md
 *     non-negotiable §1)
 *   - keeps the BE response shape opaque — NO projection / mapping. On
 *     `ready` it returns `envelope.data` (a `ReservationListItemDto[]`)
 *     straight through; the grid subtask adds DTO ↔ view mapping later
 *   - passes the FE filter input verbatim as the GET query
 *     (`propertyId` / `status` / `from` / `to` / `limit` / `offset`)
 *   - uses `placeholderData: keepPreviousData` so the page doesn't flash
 *     into a loading state on every filter change (same UX reason as
 *     UsersListPage / PropertiesListPage)
 *
 * The list envelope is `{ data: ReservationListItemDto[] }` — there is NO
 * `total` field (unlike Property's `{ data, total }`), so the `ready`
 * result exposes only `data`.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { operations } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { ReservationListItemDto, ReservationsListInput } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * Internal sentinel for the 403 branch — same idiom as `useProperties`.
 * The queryFn returns this symbol on 403 so we can distinguish forbidden
 * from a generic error in the hook's status union.
 */
const FORBIDDEN = Symbol('reservations-list:forbidden');

export type UseReservationsListResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** Rows straight from the envelope — no projection (grid maps later). */
      readonly data: readonly ReservationListItemDto[];
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly refetch: () => void;
    };

/**
 * Closed status-filter query type, DERIVED from the generated contract —
 * NOT re-declared here (CLAUDE.md FE §4: validators/contracts are the
 * single source; never hand-write a wire enum locally). If the reservation
 * status set changes, this alias follows the regenerated OpenAPI types
 * automatically. The FE input types `status` as the open `readonly string[]`
 * for ergonomics, so it is narrowed to this contract-derived union at the
 * request boundary (the BE validates membership regardless).
 */
type ReservationsStatusQuery = NonNullable<
  NonNullable<
    operations['ReservationsController_list']['parameters']['query']
  >['status']
>;

export function useReservationsList(
  input: ReservationsListInput = {},
): UseReservationsListResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...bookingQueryKeys.list({}), orgSlug, input] as const,
    enabled: orgSlug !== null,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiClient.GET('/organizations/{slug}/reservations', {
        params: {
          path: { slug: orgSlug as string },
          // Only pass params the caller set; the BE applies page defaults
          // for absent `limit` / `offset`. Undefined keys are omitted from
          // the serialized URL by openapi-fetch.
          query: {
            propertyId: input.propertyId,
            status:
              input.status === undefined
                ? undefined
                : // open FE filter input → contract-derived query union
                  // (single downcast; the BE validates membership regardless).
                  ([...input.status] as ReservationsStatusQuery),
            from: input.from,
            to: input.to,
            limit: input.limit,
            offset: input.offset,
          },
        },
      });
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }

  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    refetch,
  };
}
